import {onBeforeUnmount} from 'vue';
import {driver, type DriveStep, type Driver, type Config} from 'driver.js';
// eslint-disable-next-line import-x/no-internal-modules
import 'driver.js/dist/driver.css';
import {useI18n} from 'vue-i18n';
import {cleanupDriverDOM, TOUR_CONFIG, TOUR_NAMES, type TourName} from '@/ts/utils/tour-constants';
import {useTourStore} from '@/ts/store/tour-store';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useUserStore} from '@/ts/store/user-store';

/** Configuration for initializing a product tour */
export interface TourOptions {
  enabled?: boolean; // Gate by GDPR consent / feature flag
  tourName: TourName;
  buttonKeysPrefix?: string; // i18n prefix for button labels (default: 'onboardingTour')
  paginationStyle?: 'dots' | 'text'; // 'dots' shows step dots, 'text' shows "1 of 6" (default: 'dots')
}

/** Definition of a single tour step with target element, i18n keys, and popover positioning */
export interface TourStep {
  element: string;
  titleKey: string;
  descriptionKey?: string; // Optional when using customContent
  customContent?: string; // Custom HTML content (used instead of descriptionKey)
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  popoverClass?: string; // Additional CSS class for the popover
}

/** Creates a guided product tour using Driver.js with pagination, persistence, and cleanup on unmount */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useTour(options: TourOptions) {
  const {t} = useI18n();
  const tourStore = useTourStore();
  const api = useApi();
  const userStore = useUserStore();
  let driverInstance: Driver | null = null;
  let currentDriverRef: Driver | null = null; // Keep reference to current active driver

  const {tourName} = options;
  const tourConfig = TOUR_CONFIG[tourName];
  const btnPrefix = options.buttonKeysPrefix ?? 'onboardingTour';
  const paginationStyle = options.paginationStyle ?? 'dots';

  const hasSeenTour = (): boolean => {
    return tourStore.hasTourBeenSeen(tourName);
  };

  /**
   * Update BE version for a tour. Currently only onboardingTour has BE support.
   * Future tours will need their own BE fields or a generic tourVersions map.
   */
  const updateBEVersion = async(version: number): Promise<void> => {
    // Only onboardingTour has BE support currently
    if (tourName === TOUR_NAMES.onboardingTour) {
      try {
        // IPD-2517: when the onboarding wizard completes for the first time
        // (i.e. `legalDocsHintShown` is still unset — null or absent), arm the
        // legal-docs sidebar hint to appear on the NEXT login by persisting
        // the flag as `false`. We deliberately DO NOT mutate the local store
        // here — doing so would flip `useLegalDocsHint.shouldShow` to true in
        // the same session and the hint would pop up immediately after the
        // user finishes the wizard, double-dipping on the same UX. The next
        // `GET /me` (on the user's next session) will load the persisted
        // `false` and the hint will render then, as intended. Subsequent
        // wizard re-runs (version bumps) must NOT re-arm the hint.
        const armLegalDocsHint =
          version > 0 &&
          userStore.userData !== null &&
          typeof userStore.userData.legalDocsHintShown !== 'boolean';
        await api.me.updateMe({
          onboardingTourVersion: version,
          ...(armLegalDocsHint && {legalDocsHintShown: false}),
        });
        if (userStore.userData) {
          userStore.userData.onboardingTourVersion = version;
        }
      } catch {
        // Silently fail - localStorage fallback will handle it
      }
    }
    // Future tours: add BE support here when available
  };

  const setSeenTour = async(): Promise<void> => {
    // Update localStorage
    tourStore.setTourSeen(tourName, true);

    // Update BE with current tour version
    await updateBEVersion(tourConfig.version);
  };

  const resetTour = async(): Promise<void> => {
    tourStore.setTourSeen(tourName, false);

    // Reset BE tour version to 0
    await updateBEVersion(0);
  };

  // Build pagination HTML (dots or "X of Y" text)
  const buildPaginationHtml = (totalSteps: number, currentStep: number): string => {
    if (paginationStyle === 'text') {
      const ofText = t(`${btnPrefix}.progressOf`);
      return `<span class="driver-step-text">${currentStep + 1} ${ofText} ${totalSteps}</span>`;
    }
    let dots = '<div class="driver-step-dots">';
    for (let i = 0; i < totalSteps; i++) {
      const isActive = i === currentStep;
      dots += `<span class="driver-step-dot${isActive ? ' active' : ''}"></span>`;
    }
    dots += '</div>';
    return dots;
  };

  const buildSteps = (tourSteps: TourStep[]): DriveStep[] => {
    const totalSteps = tourSteps.length;
    return tourSteps.map((step, index) => {
      const content = step.customContent ?? (step.descriptionKey ? t(step.descriptionKey) : '');
      const paginationHtml = buildPaginationHtml(totalSteps, index);
      const isLastStep = index === totalSteps - 1;
      return {
        element: step.element,
        popover: {
          title: t(step.titleKey),
          description: content,
          side: step.side ?? 'bottom',
          align: step.align ?? 'start',
          ...(step.popoverClass && {popoverClass: step.popoverClass}),
          // Inject dots into the footer and style buttons via onPopoverRender
          onPopoverRender: (popover: {wrapper: HTMLElement}): void => {
            // Normalize popover transform to prevent inconsistent positioning
            // Use setTimeout to ensure it runs after driver.js sets its transform
            setTimeout(() => {
              popover.wrapper.style.transform = 'translateY(0px)';
            }, 0);

            const footer = popover.wrapper.querySelector('.driver-popover-footer');
            if (footer) {
              // Insert pagination at the beginning of footer
              footer.insertAdjacentHTML('afterbegin', paginationHtml);

              // Style the buttons with pill shape
              const prevBtn = footer.querySelector('.driver-popover-prev-btn') as HTMLElement | null;
              const nextBtn = footer.querySelector('.driver-popover-next-btn') as HTMLElement | null;

              if (prevBtn) {
                prevBtn.style.cssText = `
                  padding: 8px 20px !important;
                  border-radius: 100px !important;
                  background-color: var(--Light-Scheme-Neutral-UI-neutral-ui-10, #E8E4E0) !important;
                  color: var(--Light-Scheme-Neutral-UI-neutral-ui-100, #20282E) !important;
                  border: none !important;
                  box-shadow: none !important;
                  text-shadow: none !important;
                  cursor: pointer !important;
                  font-family: var(--Font-Primary-Font) !important;
                  font-size: 14px !important;
                  font-weight: 500 !important;
                `;
              }

              if (nextBtn) {
                nextBtn.style.cssText = `
                  padding: 8px 20px !important;
                  border-radius: 100px !important;
                  background-color: var(--Light-Scheme-Secondary-secondary-color-1, #BBC5D6) !important;
                  color: var(--Light-Scheme-Neutral-UI-neutral-ui-100, #20282E) !important;
                  border: none !important;
                  box-shadow: none !important;
                  text-shadow: none !important;
                  cursor: pointer !important;
                  font-family: var(--Font-Primary-Font) !important;
                  font-size: 14px !important;
                  font-weight: 500 !important;
                `;
                // Update text for last step
                if (isLastStep) {
                  nextBtn.textContent = t(`${btnPrefix}.buttons.finish`);
                }
              }
            }

            // Style the close button with inline x-close SVG icon
            const closeBtn = popover.wrapper.querySelector('.driver-popover-close-btn') as HTMLElement | null;
            if (closeBtn) {
              // Inline SVG for x-close icon with neutral-40 color
              const strokeColor = 'var(--Light-Scheme-Neutral-UI-neutral-ui-40, #B6B6B6)';
              closeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="${strokeColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>`;
              closeBtn.style.cssText = `
                position: absolute;
                top: 16px;
                right: 16px;
                width: 20px;
                height: 20px;
                padding: 0;
                background: transparent;
                border: none;
                cursor: pointer;
                pointer-events: auto;
                z-index: 10;
              `;
            }

            // Adjust title padding to account for close button
            const title = popover.wrapper.querySelector('.driver-popover-title') as HTMLElement | null;
            if (title) {
              title.style.paddingRight = '48px'; // Make room for close button
            }
          },
        },
      };
    });
  };

  const createDriver = (tourSteps: TourStep[], config?: Partial<Config>): Driver => {
    const steps = buildSteps(tourSteps);

    // Track if tour was closed via button (Close/Finish) vs other means
    let closedViaButton = false;

    // Preserve original callbacks before merging
    const originalOnDestroyed = config?.onDestroyed;
    const originalOnCloseClick = config?.onCloseClick;
    const originalOnNextClick = config?.onNextClick;
    const originalOnPrevClick = config?.onPrevClick;

    // Build config WITHOUT callbacks first (prefixed with _ to indicate intentionally unused)
    const {
      onDestroyed: _onDestroyed,
      onCloseClick: _onCloseClick,
      onNextClick: _onNextClick,
      onPrevClick: _onPrevClick,
      ...restConfig
    } = config ?? {};

    const finalConfig: Config = {
      showProgress: false, // Hide text progress, we use dots in description instead
      showButtons: ['next', 'previous', 'close'],
      popoverClass: 'balm-tour-popover',
      allowClose: false, // Disable closing by clicking outside - only via buttons
      stagePadding: 10,
      overlayColor: 'black',
      overlayOpacity: 0.45,
      smoothScroll: true, // Smooth scroll to element
      disableActiveInteraction: true, // Prevent clicking on highlighted element during tour
      allowKeyboardControl: true,
      nextBtnText: t(`${btnPrefix}.buttons.next`),
      prevBtnText: t(`${btnPrefix}.buttons.back`),
      doneBtnText: t(`${btnPrefix}.buttons.finish`),
      steps,
      // Spread custom config (without callbacks)
      ...restConfig,
      // Now add merged callbacks that won't be overridden
       
      onDestroyed: (element?: Element, step?: DriveStep, opts?: unknown) => {
        // Only mark as seen if closed via button (Close or Finish)
        if (closedViaButton) {
          void setSeenTour(); // Fire and forget - async but we don't need to wait
        }
        // Clean up after Driver.js is done with a longer delay
        setTimeout(() => {
          cleanupDriverDOM();
          if (originalOnDestroyed) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            originalOnDestroyed(element, step as any, opts as any);
          }
        }, 300);
      },
       
      onCloseClick: (element?: Element, step?: DriveStep, opts?: unknown) => {
        // Mark that tour was closed via button
        closedViaButton = true;
        // Manually destroy the driver when close is clicked
        if (driverInstance) {
          driverInstance.destroy();
        }
        if (originalOnCloseClick) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          originalOnCloseClick(element, step as any, opts as any);
        }
      },
       
      onNextClick: (element?: Element, step?: DriveStep, opts?: unknown) => {
        // Check if this is the last step (Finish button)
        if (driverInstance?.isLastStep()) {
          // Mark that tour was closed via button
          closedViaButton = true;
          driverInstance.destroy();
        } else {
          // Move to next step
          driverInstance?.moveNext();
        }
        if (originalOnNextClick) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          originalOnNextClick(element, step as any, opts as any);
        }
      },
       
      onPrevClick: (element?: Element, step?: DriveStep, opts?: unknown) => {
        driverInstance?.movePrevious();
        if (originalOnPrevClick) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          originalOnPrevClick(element, step as any, opts as any);
        }
      },
    };

    return driver(finalConfig);
  };

  const startTour = (tourSteps: TourStep[], config?: Partial<Config>): Driver | null => {
    if (options.enabled === false) {
      return null;
    }

    if (hasSeenTour()) {
      return null;
    }

    // Filter out steps whose target elements don't exist in the DOM
    const availableSteps = tourSteps.filter((step) => document.querySelector(step.element));

    if (availableSteps.length === 0) {
      return null;
    }

    // Reset cleared flag in case user logged out and back in
    tourStore.resetClearedFlag();

    // Clean up existing instance
    if (driverInstance) {
      driverInstance.destroy();
    }

    driverInstance = createDriver(availableSteps, config);
    currentDriverRef = driverInstance; // Store reference
    driverInstance.drive();
    return driverInstance;
  };

  const startTourForced = (tourSteps: TourStep[], config?: Partial<Config>): void => {
    // Filter out steps whose target elements don't exist in the DOM
    const availableSteps = tourSteps.filter((step) => document.querySelector(step.element));

    if (availableSteps.length === 0) {
      return;
    }

    // Reset cleared flag in case user logged out and back in
    tourStore.resetClearedFlag();

    // Clean up existing instance
    if (driverInstance) {
      driverInstance.destroy();
    }

    driverInstance = createDriver(availableSteps, config);
    // Recorded like the scheduled one: `getCurrentDriver` is how a caller asks whether a tour is on
    // screen before opening another, and a forced tour is on screen just as much.
    currentDriverRef = driverInstance;
    driverInstance.drive();
  };

  const destroyTour = (): void => {
    if (currentDriverRef) {
      currentDriverRef.destroy();
      currentDriverRef = null;
    }
    if (driverInstance) {
      driverInstance.destroy();
      driverInstance = null;
    }
  };

  onBeforeUnmount(() => {
    destroyTour();
  });

  return {
    startTour,
    startTourForced,
    resetTour,
    hasSeenTour,
    setSeenTour,
    destroyTour,
    cleanupDriverDOM,
    getCurrentDriver: (): Driver | null => currentDriverRef,
  };
}
